import React from "react";
import { motion } from "framer-motion";
import { Sunrise, Sunset, BookOpen, GraduationCap } from "lucide-react";
import Layout from "./Layout/Layout";
import IMGHERO from "../assets/Home3.jpg";
import Program from "./Program";

const Home = () => {
  return (
    <Layout>
      {/* HERO SECTION */}
      <section className="relative w-full h-[85vh] flex items-center justify-center overflow-hidden">
        <img
          src={IMGHERO}
          alt="Temple Background"
          className="absolute inset-0 w-full h-full object-cover brightness-75 scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-amber-950/40 via-stone-950/60 to-stone-950"></div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="relative z-10 text-center text-white px-6 max-w-4xl"
        >
          <span className="text-[#ffd27f] font-semibold text-sm md:text-base tracking-[0.25em] uppercase mb-3 block">
            Brahma-Madhva-Gaudiya Sampradaya (ISKCON)
          </span>
          <h1 className="text-4xl sm:text-6xl font-bold font-serif mb-6 drop-shadow-2xl text-[#ffd27f] tracking-wide leading-tight">
            Radha Krishna Gram Vikas Kendra
          </h1>
          <p className="text-lg md:text-xl font-light text-stone-200 max-w-2xl mx-auto leading-relaxed">
            A divine space dedicated to spreading spiritual consciousness, nurturing cultural values, and uplifting rural communities.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#schedule"
              className="w-full sm:w-auto bg-[#ffd27f] text-amber-950 hover:bg-white px-8 py-3 rounded-full font-bold shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 tracking-wider uppercase text-sm"
            >
              Daily Schedule
            </a>
            <a
              href="#programs"
              className="w-full sm:w-auto border border-stone-300 text-white hover:bg-white/10 px-8 py-3 rounded-full font-bold shadow-md transition-all duration-300 transform hover:-translate-y-0.5 tracking-wider uppercase text-sm"
            >
              Explore Programs
            </a>
          </div>
        </motion.div>
      </section>

      {/* IMPACT STATS ROW */}
      <section className="bg-[#fffbf4] border-y border-amber-200/60 py-14 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(245,158,11,0.03),transparent_70%)] pointer-events-none"></div>
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center relative z-10">
          <div>
            <p className="text-4xl md:text-5xl font-serif font-bold text-amber-700 mb-2">500+</p>
            <p className="text-stone-600 text-xs md:text-sm font-semibold uppercase tracking-widest">Children Coached</p>
          </div>
          <div>
            <p className="text-4xl md:text-5xl font-serif font-bold text-amber-700 mb-2">5+</p>
            <p className="text-stone-600 text-xs md:text-sm font-semibold uppercase tracking-widest">Villages Served</p>
          </div>
          <div>
            <p className="text-4xl md:text-5xl font-serif font-bold text-amber-700 mb-2">100%</p>
            <p className="text-stone-600 text-xs md:text-sm font-semibold uppercase tracking-widest">Free Education</p>
          </div>
          <div>
            <p className="text-4xl md:text-5xl font-serif font-bold text-amber-700 mb-2">4+</p>
            <p className="text-stone-600 text-xs md:text-sm font-semibold uppercase tracking-widest">Core Initiatives</p>
          </div>
        </div>
      </section>

      {/* PROGRAM SCHEDULE */}
      <section
        id="schedule"
        className="py-24 px-6 bg-gradient-to-b from-[#fffaf0] to-[#fff4e3]"
      >
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto text-center"
        >
          <span className="text-amber-800 font-semibold text-xs tracking-[0.2em] uppercase mb-2 block">
            Temple Timings
          </span>
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-14 text-amber-900 tracking-wide">
            🪔 Daily Spiritual Schedule 🪔
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
            <div className="bg-white border border-amber-100 rounded-3xl shadow-md p-6 hover:shadow-xl hover:border-amber-200 transition-all duration-300 flex items-start gap-5">
              <div className="bg-amber-100 text-amber-900 p-4 rounded-2xl shrink-0">
                <Sunrise size={28} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-amber-950 mb-1">🌅 Mangal Aarti</h3>
                <p className="text-stone-500 text-sm md:text-base leading-relaxed mb-1">Morning prayers, deity worship, and spiritual invocation.</p>
                <span className="text-amber-700 font-bold text-sm tracking-wider uppercase">4:30 AM to 5:15 AM</span>
              </div>
            </div>

            <div className="bg-white border border-amber-100 rounded-3xl shadow-md p-6 hover:shadow-xl hover:border-amber-200 transition-all duration-300 flex items-start gap-5">
              <div className="bg-amber-100 text-amber-900 p-4 rounded-2xl shrink-0">
                <Sunset size={28} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-amber-950 mb-1">🌇 Gaura Aarti</h3>
                <p className="text-stone-500 text-sm md:text-base leading-relaxed mb-1">Evening congregational chanting, kirtan, and deity worship.</p>
                <span className="text-amber-700 font-bold text-sm tracking-wider uppercase">8:00 PM to 8:30 PM</span>
              </div>
            </div>

            <div className="bg-white border border-amber-100 rounded-3xl shadow-md p-6 hover:shadow-xl hover:border-amber-200 transition-all duration-300 flex items-start gap-5">
              <div className="bg-amber-100 text-amber-900 p-4 rounded-2xl shrink-0">
                <BookOpen size={28} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-amber-950 mb-1">📖 Monday Bhagavad Gita Class</h3>
                <p className="text-stone-500 text-sm md:text-base leading-relaxed mb-1">Systematic study of the philosophical teachings of Bhagavad Gita.</p>
                <span className="text-amber-700 font-bold text-sm tracking-wider uppercase">8:00 PM to 9:00 PM</span>
              </div>
            </div>

            <div className="bg-white border border-amber-100 rounded-3xl shadow-md p-6 hover:shadow-xl hover:border-amber-200 transition-all duration-300 flex items-start gap-5">
              <div className="bg-amber-100 text-amber-900 p-4 rounded-2xl shrink-0">
                <GraduationCap size={28} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-amber-950 mb-1">🎓 Free Coaching Classes</h3>
                <p className="text-stone-500 text-sm md:text-base leading-relaxed mb-1">Academic tuition classes for local village students from grades 1 to 8.</p>
                <span className="text-amber-700 font-bold text-sm tracking-wider uppercase">3:00 PM to 5:00 PM</span>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* PROGRAM COMPONENT */}
      <div id="programs">
        <Program />
      </div>
    </Layout>
  );
};

export default Home;
